import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Page, Button, Spinner, FullBox, TableList, Typography, DropdownMenu, TableListActions, useOverlayContext } from 'seagull';
import { Edit, Refresh, Trash } from 'seagull/icons';
import ExternalLinkButton from '~/components/ExternalLinkButton';
import RestfulWrapper from '~/components/RestfulWrapper/RestfulWrapper';
import { useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { formatShortMonthUSDate } from '~/utils';
import { APIKeysEmptyState } from './APIKeysEmptyState';
import { APIKeysSortField, apiKeysColumnDefs, apiKeysHeaderItems, formatAPIKeyValue } from './APIKeysPage.utils';
import { DeactivateAPIKeyModal } from './modals/DeactivateAPIKeyModal';
import GenerateAPIKeyModal from './modals/GenerateAPIKeyModal';
import { RegenerateAPIKeyModal } from './modals/RegenerateAPIKeyModal';
import { RenameAPIKeyModal } from './modals/RenameAPIKeyModal';
const normalizeName = (name)=>name.trim().toLowerCase();
const APIKeysSubheader = ()=>{
    const { t: texts } = useLocaleKeys('pages.apiKeysPage');
    return /*#__PURE__*/ _jsxs(Typography, {
        children: [
            texts.accessSubheader(),
            ' ',
            /*#__PURE__*/ _jsx(ExternalLinkButton, {
                href: "https://docs.sayanchor.com",
                underline: "none",
                openInNewTab: true,
                testId: "anchor-api-docs-link",
                bi: {
                    name: 'anchor_api_docs_link'
                },
                children: texts.anchorApi()
            })
        ]
    });
};
export function getIsNameValid(name, keys, currentName) {
    const trimmedName = normalizeName(name);
    if (currentName && trimmedName === normalizeName(currentName)) {
        return true;
    }
    return !keys.find((key)=>normalizeName(key.name) === trimmedName);
}
const APIKeysPage = ()=>{
    const { t: texts } = useLocaleKeys('pages.apiKeysPage');
    const overlayContext = useOverlayContext();
    const { api } = useStoreActions();
    const [sort, setSort] = useState({
        field: APIKeysSortField.Name,
        direction: 'asc'
    });
    const handleGenerateAPIKey = (keys, refresh)=>{
        overlayContext.showModal({
            biName: 'generate_api_key_modal',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(GenerateAPIKeyModal, {
                    getIsNameValid: (name)=>getIsNameValid(name, keys),
                    dismissModal: dismissModal,
                    refresh: refresh
                });
            },
            closeOnClickOutside: true,
            closeOnEscapeKey: true
        });
    };
    const fetchAPIKeys = ()=>api.business.getAPIKeys({
            sortBy: sort.field,
            sortDirection: sort.direction
        });
    const handleSortChange = (tableSort)=>{
        if (!tableSort) {
            setSort({
                field: APIKeysSortField.Name,
                direction: 'asc'
            });
        } else {
            const fieldMap = {
                0: APIKeysSortField.Name,
                2: APIKeysSortField.CreatedOn,
                3: APIKeysSortField.UpdatedOn
            };
            setSort({
                field: fieldMap[tableSort.colIndex],
                direction: tableSort.sort
            });
        }
    };
    return /*#__PURE__*/ _jsx(RestfulWrapper, {
        fetch: fetchAPIKeys,
        renderLoader: ()=>/*#__PURE__*/ _jsx(Page, {
                header: texts.header(),
                subheader: /*#__PURE__*/ _jsx(APIKeysSubheader, {}),
                testId: "api-keys-page",
                children: /*#__PURE__*/ _jsx(Spinner, {
                    size: 24
                })
            }),
        renderError: ()=>/*#__PURE__*/ _jsx(Page, {
                header: texts.header(),
                subheader: /*#__PURE__*/ _jsx(APIKeysSubheader, {}),
                testId: "api-keys-page",
                children: /*#__PURE__*/ _jsx(FullBox, {
                    testId: "api-keys-error",
                    children: /*#__PURE__*/ _jsx(Typography, {
                        children: texts.errorLoading()
                    })
                })
            }),
        renderChild: (param)=>{
            let { data: { keys }, refresh } = param;
            return /*#__PURE__*/ _jsx(Page, {
                header: texts.header(),
                variant: "containedHeight",
                subheader: /*#__PURE__*/ _jsx(APIKeysSubheader, {}),
                actions: keys.length > 0 ? /*#__PURE__*/ _jsx(Button, {
                    bi: {
                        name: 'generate_api_key'
                    },
                    testId: "generate-api-key-button",
                    onClick: ()=>handleGenerateAPIKey(keys, refresh),
                    children: texts.generateApiKey()
                }) : null,
                testId: "api-keys-page",
                children: keys.length > 0 ? /*#__PURE__*/ _jsx(TableList.Provider, {
                    columnDefs: apiKeysColumnDefs,
                    hasActionColumn: true,
                    sort: {
                        sort: sort.direction,
                        colIndex: apiKeysHeaderItems.findIndex((item)=>item.id === sort.field)
                    },
                    onSortChange: handleSortChange,
                    children: /*#__PURE__*/ _jsx(TableList.Structure, {
                        header: /*#__PURE__*/ _jsx(TableList.Header, {
                            items: apiKeysHeaderItems,
                            testId: "api-keys-table-header"
                        }),
                        content: keys.map((key, index)=>/*#__PURE__*/ _jsx(TableList.Row, {
                                items: [
                                    {
                                        value: key.name,
                                        testId: "api-key-name-".concat(index)
                                    },
                                    {
                                        value: formatAPIKeyValue(key.id),
                                        testId: "api-key-id-".concat(index)
                                    },
                                    {
                                        value: formatShortMonthUSDate(new Date(key.createdOn)),
                                        testId: "api-key-created-".concat(index)
                                    },
                                    {
                                        value: formatShortMonthUSDate(new Date(key.updatedOn)),
                                        testId: "api-key-updated-".concat(index)
                                    }
                                ],
                                actionColumn: /*#__PURE__*/ _jsxs(TableListActions, {
                                    testId: "api-key-actions-".concat(index),
                                    children: [
                                        /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                                            bi: {
                                                name: 'regenerate_api_key'
                                            },
                                            iconStart: /*#__PURE__*/ _jsx(Refresh, {}),
                                            onClick: ()=>{
                                                overlayContext.showModal({
                                                    biName: 'regenerate_api_key_modal',
                                                    content: (param)=>{
                                                        let { dismissModal } = param;
                                                        return /*#__PURE__*/ _jsx(RegenerateAPIKeyModal, {
                                                            apiKey: keys[index],
                                                            dismissModal: dismissModal,
                                                            refresh: refresh
                                                        });
                                                    },
                                                    closeOnClickOutside: true,
                                                    closeOnEscapeKey: true
                                                });
                                            },
                                            testId: "regenerate-api-key-".concat(index),
                                            children: texts.regenerateApiKeyAction()
                                        }),
                                        /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                                            bi: {
                                                name: 'rename_api_key'
                                            },
                                            iconStart: /*#__PURE__*/ _jsx(Edit, {}),
                                            onClick: ()=>{
                                                overlayContext.showModal({
                                                    biName: 'rename_api_key_modal',
                                                    content: (param)=>{
                                                        let { dismissModal } = param;
                                                        return /*#__PURE__*/ _jsx(RenameAPIKeyModal, {
                                                            apiKey: keys[index],
                                                            getIsNameValid: (name)=>getIsNameValid(name, keys, keys[index].name),
                                                            dismissModal: dismissModal,
                                                            refresh: refresh
                                                        });
                                                    },
                                                    closeOnClickOutside: true,
                                                    closeOnEscapeKey: true
                                                });
                                            },
                                            testId: "rename-api-key-".concat(index),
                                            children: texts.renameAction()
                                        }),
                                        /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                                            bi: {
                                                name: 'deactivate_api_key'
                                            },
                                            skin: "error",
                                            iconStart: /*#__PURE__*/ _jsx(Trash, {}),
                                            onClick: ()=>{
                                                overlayContext.showModal({
                                                    biName: 'deactivate_api_key_modal',
                                                    content: (param)=>{
                                                        let { dismissModal } = param;
                                                        return /*#__PURE__*/ _jsx(DeactivateAPIKeyModal, {
                                                            apiKey: keys[index],
                                                            dismissModal: dismissModal,
                                                            refresh: refresh
                                                        });
                                                    },
                                                    closeOnClickOutside: true,
                                                    closeOnEscapeKey: true
                                                });
                                            },
                                            testId: "deactivate-api-key-".concat(index),
                                            children: texts.deactivateApiKeyAction()
                                        })
                                    ]
                                })
                            }, index))
                    })
                }) : /*#__PURE__*/ _jsx(APIKeysEmptyState, {
                    generateKey: ()=>handleGenerateAPIKey(keys, refresh)
                })
            });
        }
    }, "".concat(sort.field, "-").concat(sort.direction));
};
export default APIKeysPage;
